import React, { useEffect, useState } from "react";
import {
    Play, Activity, AlertTriangle, Car, Target, RefreshCw,
    Video, ChevronRight, AlertCircle,
} from "lucide-react";
import { fetchClosedLoopReport } from "../../services/LabService";

/**
 * ClosedLoopTab — surfaces the AlpaSim closed-loop simulation report.
 *
 * Closed-loop differs from our default open-loop minADE: it runs the
 * model in NVIDIA's AlpaSim simulator where each model decision
 * actually moves the simulated car. We get safety-relevant metrics
 * that open-loop can't produce: collision rate, off-road rate,
 * route completion %, etc.
 *
 * States:
 *   queued    — execution started, sim hasn't run yet
 *   running   — sim is in progress (can be minutes to hours)
 *   complete  — metrics + violation videos available
 *   failed    — sim crashed or AlpaSim was unavailable
 */
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const num = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));

const VIOLATION_DESCRIPTIONS = {
    collision_at_fault:
        "The simulated model caused a collision with another agent. Highest-priority safety failure.",
    collision_rear:
        "Another agent collided into the rear of the simulated vehicle. Often the model braked too hard or unpredictably.",
    offroad:
        "The model drove off the navigable road surface.",
    dist_to_gt_trajectory:
        "The simulated path diverged significantly from the human ground-truth trajectory (>5m).",
};

const ClosedLoopTab = ({ runId }) => {
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);
    const [polling, setPolling] = useState(false);

    const load = async () => {
        setError(null);
        try {
            const r = await fetchClosedLoopReport(runId);
            setReport(r);
        } catch (e) {
            if (e.status === 404) {
                setReport({ run_id: runId, status: "not_run" });
            } else {
                setError(e.message);
            }
        }
    };

    useEffect(() => {
        load();
    }, [runId]);

    useEffect(() => {
        if (!report) return;
        const isTerminal = ["complete", "failed", "not_run"].includes(report.status);
        if (isTerminal) {
            setPolling(false);
            return;
        }
        setPolling(true);
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [report?.status]);

    if (error) {
        return (
            <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-4 text-sm">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300 mb-1 font-semibold">
                    <AlertCircle className="w-4 h-4" /> Error loading closed-loop report
                </div>
                <div className="text-red-700 dark:text-red-300">{error}</div>
                <button onClick={load} className="mt-2 text-xs px-2.5 py-1 bg-red-600 text-white rounded">
                    Retry
                </button>
            </div>
        );
    }

    if (!report) {
        return (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                <RefreshCw className="w-4 h-4 animate-spin" /> Loading closed-loop report…
            </div>
        );
    }

    if (report.status === "not_run") {
        return (
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-6 text-center">
                <Car className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <h3 className="font-semibold mb-1">No closed-loop run for this experiment.</h3>
                <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                    Open-loop minADE measures prediction error against ground-truth waypoints.
                    Closed-loop runs the model in NVIDIA's AlpaSim simulator where each decision
                    actually moves the car — capturing collision rate, off-road rate, route
                    completion, and other safety-critical metrics.
                </p>
                <p className="text-xs text-slate-400">
                    Click <strong>Run closed-loop</strong> in the experiment header to start.
                </p>
            </div>
        );
    }

    if (report.status === "queued" || report.status === "running") {
        return (
            <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/20 p-6 text-center">
                <Activity className="w-10 h-10 text-blue-500 mx-auto mb-3 animate-pulse" />
                <h3 className="font-semibold mb-1">
                    Closed-loop simulation {report.status === "queued" ? "queued" : "in progress"}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                    AlpaSim runs the simulator microservices, driver policy, and scoring rubric.
                    Typically 5-30 min depending on scene count.
                </p>
                {polling ? (
                    <p className="text-xs text-slate-400 mt-3">Polling every 15s…</p>
                ) : null}
            </div>
        );
    }

    if (report.status === "failed") {
        return (
            <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50/40 dark:bg-red-950/20 p-6">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300 mb-2 font-semibold">
                    <AlertCircle className="w-4 h-4" /> Closed-loop run failed
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                    The AlpaSim session ended in an error state. Check Step Functions execution
                    logs for details. Most common causes: AlpaSim Docker image pull timeout,
                    HF token expired, or a scene_id that doesn't exist.
                </p>
            </div>
        );
    }

    // status === "complete"
    const m = report.metrics || {};
    const videos = report.violation_videos || [];
    const collisionRate = m.collision_at_fault_rate;
    const offroadRate = m.offroad_rate;
    const routeCompletion = m.route_completion_mean;
    const distMean = m.dist_to_gt_trajectory_mean;

    const verdict = _safetyVerdict(collisionRate, offroadRate);

    return (
        <div>
            {/* Safety verdict banner */}
            <div
                className={`rounded-lg border-l-4 p-4 mb-4 ${verdict.bgClass}`}
            >
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                    Closed-loop safety verdict
                </div>
                <div className="text-lg font-bold flex items-center gap-2">
                    {verdict.icon} {verdict.label}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">{verdict.detail}</div>
            </div>

            {/* Key metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <MetricCard
                    icon={<AlertTriangle className="w-4 h-4 text-red-500" />}
                    label="Collision (at fault)"
                    value={pct(collisionRate)}
                    sub={`${m.n_rollouts || 0} rollouts`}
                />
                <MetricCard
                    icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}
                    label="Off-road"
                    value={pct(offroadRate)}
                    sub="of rollouts"
                />
                <MetricCard
                    icon={<Target className="w-4 h-4 text-emerald-500" />}
                    label="Route completion"
                    value={pct(routeCompletion)}
                    sub="mean across rollouts"
                />
                <MetricCard
                    icon={<Activity className="w-4 h-4 text-indigo-500" />}
                    label="Dist to GT trajectory"
                    value={`${num(distMean, 2)}m`}
                    sub={`p95 ${num(m.dist_to_gt_trajectory_p95, 2)}m`}
                />
            </div>

            {/* Violation video grid */}
            {videos.length > 0 ? (
                <div className="mt-6">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        Violation playback ({videos.length})
                    </h4>
                    <p className="text-xs text-slate-500 mb-3">
                        Click any clip to watch the simulated drive at the moment of the violation.
                    </p>
                    <ViolationVideoGrid videos={videos} />
                </div>
            ) : (
                <div className="text-sm text-slate-500 text-center py-4 mt-4">
                    <span className="inline-flex items-center gap-2">
                        <Target className="w-4 h-4 text-emerald-500" />
                        Zero violations detected in this run.
                    </span>
                </div>
            )}

            {/* Aggregate report link */}
            {report.aggregate_report_url ? (
                <div className="mt-6 text-xs">
                    <a
                        href={report.aggregate_report_url}
                        target="_blank" rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
                    >
                        Download full AlpaSim aggregate report (TXT) <ChevronRight className="w-3.5 h-3.5" />
                    </a>
                </div>
            ) : null}
        </div>
    );
};

const MetricCard = ({ icon, label, value, sub }) => (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
            {icon} {label}
        </div>
        <div className="text-2xl font-bold font-mono">{value}</div>
        <div className="text-xs text-slate-400 mt-1">{sub}</div>
    </div>
);

const ViolationVideoGrid = ({ videos }) => {
    const [selected, setSelected] = useState(null);
    // Group by violation type
    const grouped = videos.reduce((acc, v) => {
        (acc[v.type] = acc[v.type] || []).push(v);
        return acc;
    }, {});
    return (
        <div className="space-y-4">
            {Object.entries(grouped).map(([type, vids]) => (
                <div key={type}>
                    <div className="text-xs font-mono text-slate-500 mb-2">
                        {type.replace(/_/g, " ")} ({vids.length})
                        {VIOLATION_DESCRIPTIONS[type] ? (
                            <span className="text-slate-400 ml-2 italic">
                                — {VIOLATION_DESCRIPTIONS[type]}
                            </span>
                        ) : null}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {vids.slice(0, 12).map((v) => (
                            <button
                                key={v.filename}
                                onClick={() => setSelected(v)}
                                className="text-xs border border-slate-200 dark:border-slate-700 rounded p-2 hover:bg-slate-50 dark:hover:bg-slate-800 text-left"
                                title={v.filename}
                            >
                                <div className="flex items-center gap-1 text-slate-700 dark:text-slate-300 mb-1">
                                    <Play className="w-3 h-3" />
                                    <span className="font-mono truncate">{v.scene_id.slice(0, 14)}…</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            ))}
            {selected ? (
                <div
                    className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
                >
                    <div className="bg-white dark:bg-slate-900 rounded-lg max-w-4xl w-full">
                        <div className="p-3 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
                            <div>
                                <div className="text-sm font-semibold">{selected.type.replace(/_/g, " ")}</div>
                                <div className="text-xs font-mono text-slate-500">{selected.scene_id}</div>
                            </div>
                            <button
                                onClick={() => setSelected(null)}
                                className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
                            >
                                Close
                            </button>
                        </div>
                        <video src={selected.video_url} controls autoPlay className="w-full" />
                    </div>
                </div>
            ) : null}
        </div>
    );
};

function _safetyVerdict(collisionRate, offroadRate) {
    if (collisionRate == null && offroadRate == null) {
        return {
            label: "Inconclusive",
            detail: "No safety metrics computed.",
            icon: "—",
            bgClass: "border-slate-400 bg-slate-50 dark:bg-slate-900",
        };
    }
    if ((collisionRate || 0) > 0.05) {
        return {
            label: "Unsafe",
            detail: `Collision rate ${(collisionRate * 100).toFixed(1)}% — above safety threshold (5%).`,
            icon: "🚨",
            bgClass: "border-red-500 bg-red-50 dark:bg-red-950/30",
        };
    }
    if ((collisionRate || 0) > 0.01 || (offroadRate || 0) > 0.05) {
        return {
            label: "Needs improvement",
            detail: "Some collisions or off-road events — review the violation videos.",
            icon: "⚠️",
            bgClass: "border-amber-500 bg-amber-50 dark:bg-amber-950/30",
        };
    }
    return {
        label: "Safe in tested scenarios",
        detail: "No safety-critical violations detected in this battery.",
        icon: "✅",
        bgClass: "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30",
    };
}

export default ClosedLoopTab;
